import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Target, Calendar, CheckCircle, AlertCircle, Clock, X, Info } from 'lucide-react';

// Export Goal interface for use by parent components
export interface Goal {
  id: string;
  title: string;
  description?: string;
  progress: number; // 0-100 percentage
  target_value?: number;
  current_value?: number;
  deadline?: string;
  priority: 'low' | 'medium' | 'high';
  status: 'active' | 'completed' | 'overdue' | 'paused';
  category?: string;
}

interface GoalTrackingWidgetProps {
  id: string;
  title: string;
  goals: Goal[];
  show_completed?: boolean;
  max_goals?: number;
  compact_mode?: boolean;
  onGoalClick?: (goal: Goal) => void; // Add callback for goal clicks
}

const GoalTrackingWidget: React.FC<GoalTrackingWidgetProps> = ({
  id,
  title,
  goals = [],
  show_completed = false,
  max_goals = 5,
  compact_mode = false,
  onGoalClick
}) => {
  // Filter and sort goals
  const filteredGoals = goals
    .filter(goal => show_completed || goal.status !== 'completed')
    .sort((a, b) => {
      // Sort by priority (high > medium > low) and then by progress
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return a.progress - b.progress; // Show least complete first
    })
    .slice(0, max_goals);

  // Get priority color
  const getPriorityColor = (priority: Goal['priority']) => {
    switch (priority) {
      case 'high': return 'text-red-600 dark:text-red-400';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400';
      case 'low': return 'text-green-600 dark:text-green-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Get status icon
  const getStatusIcon = (status: Goal['status'], progress: number) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'paused':
        return <Clock className="w-4 h-4 text-gray-600" />;
      default:
        return progress >= 90 ? (
          <CheckCircle className="w-4 h-4 text-green-600" />
        ) : progress >= 50 ? (
          <Target className="w-4 h-4 text-blue-600" />
        ) : (
          <Target className="w-4 h-4 text-gray-600" />
        );
    }
  };

  // Get progress bar color
  const getProgressColor = (progress: number, status: Goal['status']) => {
    if (status === 'completed') return 'bg-green-500';
    if (status === 'overdue') return 'bg-red-500';
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 70) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  // Format deadline
  const formatDeadline = (deadline?: string) => {
    if (!deadline) return null;
    
    const date = new Date(deadline);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <span className="text-red-600 dark:text-red-400">Overdue</span>;
    } else if (diffDays === 0) {
      return <span className="text-orange-600 dark:text-orange-400">Due today</span>;
    } else if (diffDays <= 7) {
      return <span className="text-yellow-600 dark:text-yellow-400">{diffDays} days left</span>;
    } else {
      return <span className="text-gray-500 dark:text-gray-400">{diffDays} days left</span>;
    }
  };

  // Render compact mode
  if (compact_mode) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center">
            <Target className="h-4 w-4 mr-2" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {filteredGoals.length === 0 ? (
            <div className="text-center py-4">
              <Target className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No active goals</p>
            </div>
          ) : (
            filteredGoals.map((goal) => (
              <div key={goal.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(goal.status, goal.progress)}
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {goal.title}
                    </span>
                  </div>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {goal.progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(goal.progress, goal.status)}`}
                    style={{ width: `${Math.min(goal.progress, 100)}%` }}
                  ></div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    );
  }

  // Render full mode
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold flex items-center justify-between">
          <div className="flex items-center">
            <Target className="h-5 w-5 mr-2" />
            {title}
          </div>
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
            {filteredGoals.filter(g => g.status === 'completed').length}/{filteredGoals.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {filteredGoals.length === 0 ? (
          <div className="text-center py-6">
            <Target className="w-10 h-10 mx-auto text-gray-400 mb-2" />
            <h3 className="text-base font-medium text-gray-600 dark:text-gray-400 mb-1">
              No active goals
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Set some goals to track your progress
            </p>
          </div>
        ) : (
          filteredGoals.map((goal) => (
            <div 
              key={goal.id} 
              className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              onClick={() => onGoalClick?.(goal)}
            >
              {/* Full Width Goal Layout */}
              <div className="flex items-center justify-between w-full">
                {/* Left Side - Icon and Title */}
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getStatusIcon(goal.status, goal.progress)}
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {goal.title}
                  </h4>
                  {goal.category && (
                    <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded text-xs flex-shrink-0">
                      {goal.category}
                    </span>
                  )}
                </div>

                {/* Right Side - Info Icon */}
                <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0" />
              </div>
              
              {/* Progress Bar - Full Width */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(goal.progress, goal.status)}`}
                  style={{ width: `${Math.min(goal.progress, 100)}%` }}
                ></div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default GoalTrackingWidget; 